import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, CheckCircle, Phone, Star } from "@phosphor-icons/react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ChatWidget from "../components/ChatWidget";
import FloatingActions from "../components/FloatingActions";
import BookingForm from "../components/BookingForm";
import Marquee from "../components/Marquee";
import Seo from "../components/Seo";
import { getSuburbBySlug, SUBURBS_SEO } from "../lib/suburbs";
import { SERVICES, CONTACT } from "../lib/constants";
import { api } from "../lib/api";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/f356e486-2013-43a6-a960-a50c27505bc4/images/29383ace10214344ab9dbf2c4cf77c8c3d92666c7230385a3c21e76adff9e659.png";

export default function SuburbPage() {
  const { slug } = useParams();
  const suburb = getSuburbBySlug(slug);
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    if (!suburb) return;
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, [suburb]);

  if (!suburb) return <Navigate to="/" replace />;

  const seoTitle = `Painters in ${suburb.name} | Free Quote in 24 hrs | The Melbourne Painters`;
  const seoDesc = `Premium painters in ${suburb.name} VIC ${suburb.postcode}. ${suburb.blurb} Free on-site quotes within 24 hours · Fixed price · Dulux-accredited · Fully insured.`;
  const seoKeywords = `painters ${suburb.name}, ${suburb.name} painters, house painters ${suburb.name}, interior painting ${suburb.name}, exterior painting ${suburb.name}, painter near me ${suburb.postcode}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Painter",
        "@id": `https://themelbournepainters.com.au/painters/${suburb.slug}#business`,
        "name": `The Melbourne Painters — ${suburb.name}`,
        "image": HERO_IMG,
        "url": `https://themelbournepainters.com.au/painters/${suburb.slug}`,
        "telephone": "+61426677479",
        "email": CONTACT.email,
        "priceRange": "$$",
        "areaServed": { "@type": "Place", "name": `${suburb.name}, VIC ${suburb.postcode}` },
        "address": { "@type": "PostalAddress", "addressLocality": suburb.name, "postalCode": suburb.postcode, "addressRegion": "VIC", "addressCountry": "AU" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "80", "bestRating": "5" },
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://themelbournepainters.com.au/" },
          { "@type": "ListItem", "position": 2, "name": "Painters", "item": "https://themelbournepainters.com.au/painters" },
          { "@type": "ListItem", "position": 3, "name": `Painters in ${suburb.name}`, "item": `https://themelbournepainters.com.au/painters/${suburb.slug}` },
        ],
      },
    ],
  };

  const nearby = SUBURBS_SEO.filter((s) => s.region === suburb.region && s.slug !== suburb.slug).slice(0, 8);
  const localProjects = projects.filter((p) => p.suburb === suburb.name);
  const showProjects = localProjects.length > 0 ? localProjects : projects.slice(0, 4);

  return (
    <div className="min-h-screen bg-[#F2F2EF] text-[#111]">
      <Seo
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        path={`/painters/${suburb.slug}`}
        image={HERO_IMG}
        jsonLd={jsonLd}
      />
      <Header />

      {/* HERO */}
      <section data-testid="suburb-hero" className="relative pt-28 md:pt-36 pb-12 md:pb-20 px-5 md:px-10 lg:px-16 overflow-hidden grain">
        <div className="grid grid-cols-12 gap-4 md:gap-6 items-end">
          <div className="col-span-12 lg:col-span-7 relative z-10">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex items-center gap-3 mb-6">
              <Link to="/" className="text-xs uppercase tracking-widest text-[#666] hover:text-[#D85C3A]">← Home</Link>
              <span className="tick" />
              <span className="eyebrow">{suburb.region} · VIC {suburb.postcode}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              data-testid="suburb-h1"
              className="font-display text-[14vw] sm:text-[12vw] lg:text-[8vw] xl:text-[7.5rem] leading-[0.86] tracking-tighter uppercase"
            >
              Painters<br />in <span className="text-[#D85C3A]">{suburb.name}</span>.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-8 max-w-xl text-lg md:text-xl text-[#222] leading-relaxed font-medium"
            >
              {suburb.blurb}
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className="mt-10 flex flex-wrap gap-4 items-center">
              <a href="#book" data-testid="suburb-cta-quote" className="btn-brut">Free quote in {suburb.name} <ArrowRight size={20} weight="bold" /></a>
              <a href={`tel:${CONTACT.phone}`} className="btn-outline-brut"><Phone size={18} weight="bold" /> {CONTACT.phoneDisplay}</a>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.45 }} className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} weight="fill" className="text-[#D85C3A]" />)}
                <span className="ml-2 text-sm font-bold">4.9 · trusted by {suburb.region} locals</span>
              </div>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="col-span-12 lg:col-span-5 relative">
            <div className="relative border-2 border-[#111] shadow-brut overflow-hidden aspect-[4/5] bg-[#1A1A1A]">
              <img src={HERO_IMG} alt={`Painters in ${suburb.name}`} className="w-full h-full object-cover" loading="eager" />
              <div className="absolute -bottom-4 -left-4 bg-[#F2F2EF] border-2 border-[#111] px-4 py-3 shadow-brut-sm">
                <div className="text-[10px] uppercase tracking-widest text-[#666]">Now booking</div>
                <div className="font-display text-lg">{suburb.name} · 24h response</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* WHY US */}
      <section className="py-20 md:py-28 px-5 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-5">
            <p className="section-num mb-3">01 — Why locals pick us</p>
            <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tighter leading-[0.92]">
              The {suburb.name} <br /> standard.<br />
              <span className="text-[#D85C3A]">No shortcuts.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { t: "Fixed-price quotes", d: `On-site within 24 hours across ${suburb.name}.` },
              { t: "Premium paints only", d: "Dulux, Wattyl, Taubmans — no rebadged seconds." },
              { t: "Tidy crews", d: "Floors covered, surfaces masked, kids and pets welcome." },
              { t: "Heritage-friendly", d: `${suburb.region} character homes painted with restraint.` },
            ].map((x) => (
              <div key={x.t} className="border-2 border-[#111] p-5 bg-[#F2F2EF]">
                <CheckCircle size={26} weight="duotone" className="text-[#D85C3A] mb-2" />
                <div className="font-display text-lg uppercase tracking-tight">{x.t}</div>
                <p className="text-sm text-[#444] mt-1">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 md:py-28 px-5 md:px-10 lg:px-16 bg-[#1A1A1A] text-[#F2F2EF] grain">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="section-num mb-3" style={{ color: "#D85C3A" }}>02 — Services in {suburb.name}</p>
            <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tighter leading-[0.92]">
              What we paint<br />in {suburb.name}.
            </h2>
          </div>
          <p className="max-w-md text-[#bbb]">From single-room interiors to full exterior weatherboard repaints — same crew, same standard.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {SERVICES.map((s, i) => (
            <a key={s.key} href="#book" className="group border-2 border-[#F2F2EF] p-6 hover:bg-[#D85C3A] transition-colors flex flex-col justify-between min-h-[180px]">
              <div className="flex items-start justify-between">
                <span className="font-display text-3xl">0{i + 1}</span>
                <ArrowUpRight size={26} weight="bold" className="opacity-50 group-hover:opacity-100 group-hover:rotate-45 transition-all" />
              </div>
              <div>
                <h3 className="font-display text-2xl uppercase tracking-tight mb-2">{s.title}</h3>
                <p className="text-sm text-[#bbb] group-hover:text-white">{s.blurb}</p>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* RECENT WORK */}
      {showProjects.length > 0 && (
        <section className="py-20 md:py-28 px-5 md:px-10 lg:px-16">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <p className="section-num mb-3">03 — Recent work</p>
              <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tighter leading-[0.92]">
                {localProjects.length > 0 ? `In ${suburb.name}` : "Across Melbourne"}.
              </h2>
            </div>
            <Link to="/" className="btn-outline-brut">All projects <ArrowRight size={18} weight="bold" /></Link>
          </div>
          <div className="grid grid-cols-12 gap-4">
            {showProjects.slice(0, 4).map((p, i) => (
              <div key={p.id} className={`border-2 border-[#111] overflow-hidden ${i === 0 ? "col-span-12 md:col-span-6 aspect-[16/10]" : "col-span-12 sm:col-span-6 md:col-span-3 aspect-[4/5]"}`}>
                <div className="relative w-full h-full">
                  <img src={p.image_url} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute inset-0 p-5 flex flex-col justify-end text-white">
                    <span className="text-[10px] uppercase tracking-widest bg-[#F2F2EF] text-[#111] px-2 py-1 border-2 border-[#111] inline-block w-fit mb-2">{p.category}</span>
                    <h3 className="font-display text-2xl uppercase tracking-tight leading-tight">{p.title}</h3>
                    {p.suburb && <div className="flex items-center gap-1 text-xs uppercase tracking-widest mt-1 opacity-80"><MapPin size={12} weight="duotone" /> {p.suburb}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* TESTIMONIAL */}
      {testimonials.length > 0 && (
        <section className="py-20 md:py-28 px-5 md:px-10 lg:px-16 bg-[#EAEAE5] border-y-2 border-[#111]">
          <p className="section-num mb-3">04 — Word from locals</p>
          <p className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05] uppercase max-w-5xl">
            "{testimonials[0].text}"
          </p>
          <div className="mt-4 text-xs uppercase tracking-widest">— {testimonials[0].name}, {testimonials[0].suburb}</div>
        </section>
      )}

      {/* BOOK */}
      <BookingForm />

      {/* NEARBY */}
      <section className="py-16 md:py-24 px-5 md:px-10 lg:px-16">
        <div className="mb-8">
          <p className="section-num mb-3">05 — Nearby</p>
          <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-[0.92]">
            Other {suburb.region} suburbs<br />we paint in.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {nearby.map((s) => (
            <Link
              to={`/painters/${s.slug}`}
              key={s.slug}
              data-testid={`nearby-${s.slug}`}
              className="border-2 border-[#111] p-4 bg-[#F2F2EF] hover:bg-[#D85C3A] hover:text-white transition-colors group"
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className="font-display text-lg uppercase tracking-tight block">{s.name}</span>
                  <span className="text-[10px] uppercase tracking-widest opacity-60">{s.postcode}</span>
                </div>
                <ArrowUpRight size={18} weight="bold" className="opacity-50 group-hover:opacity-100 group-hover:rotate-45 transition-all" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Marquee reverse />
      <Footer />
      <ChatWidget />
      <FloatingActions />
    </div>
  );
}
